import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  getFacturationById, 
  getPdfStatus, 
  generateFacturationPdf, 
  viewFacturationPdf, 
  downloadFacturationPdf,
  sendFacturationByEmail 
} from '../../service/api/facturationService';
import { toast } from 'sonner';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { formatDate } from '../../service/utils/formatters';
import { formatCurrency } from '../../service/utils/facturationUtils';

const DetailFactureModal = () => {
  const { t } = useTranslation('facturation');
  const selectedDomain = useSelectedDomain();
  const [facturation, setFacturation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfActionLoading, setPdfActionLoading] = useState(null);

  useEffect(() => {
    const modal = document.getElementById('detailFactureModal');
    if (modal) {
      modal.addEventListener('show.bs.modal', handleModalShow);
    }
    
    return () => {
      if (modal) {
        modal.removeEventListener('show.bs.modal', handleModalShow);
      }
    };
  }, []);

  const handleModalShow = async (event) => {
    const facturationId = event.relatedTarget?.getAttribute('data-facturation-id');
    if (facturationId) {
      await loadFacturationDetails(facturationId);
      await loadPdfStatus(facturationId);
    }
  };

  const loadFacturationDetails = async (facturationId) => {
    setLoading(true);
    try {
      const data = await getFacturationById(facturationId);
      setFacturation(data);
    } catch (error) {
      console.error('Error loading facturation details:', error);
      toast.error(t('errors.loadDetails'));
    } finally {
      setLoading(false);
    }
  };

  const loadPdfStatus = async (facturationId) => {
    try {
      const status = await getPdfStatus(facturationId);
      setPdfStatus(status);
    } catch (error) {
      console.error('Error loading PDF status:', error);
    }
  };

  const handleGeneratePdf = async () => {
    if (!facturation || !selectedDomain?.id) return;

    setGeneratingPdf(true);
    try {
      await generateFacturationPdf(facturation.id, selectedDomain.id);
      toast.success(t('pdfActions.generated'));
      await loadPdfStatus(facturation.id);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('errors.generatePdf'));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleViewPdf = async () => {
    if (!facturation?.id) return;

    setPdfActionLoading('view');
    try {
      const result = await viewFacturationPdf(facturation.id);
      toast.success(result.message || t('pdfActions.view.success'));
    } catch (error) {
      console.error('Error viewing PDF:', error);
      
      if (error.message.includes('non trouvé')) {
        toast.error(t('pdfActions.notFound'));
        setTimeout(() => handleGenerateAndViewPdf(), 1000);
      } else {
        toast.error(error.message || t('errors.viewPdf'));
      }
    } finally {
      setPdfActionLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!facturation?.id) return;

    setPdfActionLoading('download');
    try {
      const result = await downloadFacturationPdf(facturation.id);
      toast.success(result.message || t('pdfActions.download.success'));
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      if (error.message.includes('non trouvé')) {
        toast.error(t('pdfActions.notFound'));
        setTimeout(() => handleGenerateAndDownloadPdf(), 1000);
      } else {
        toast.error(error.message || t('errors.downloadPdf'));
      }
    } finally {
      setPdfActionLoading(null);
    }
  };

  const handleSendEmail = async (useSecureLink = true) => {
    if (!facturation?.id) return;

    setPdfActionLoading('email');
    try {
      const result = await sendFacturationByEmail(facturation.id, useSecureLink);
      
      if (result.success) {
        toast.success(result.message || t('pdfActions.email.success'));
      } else {
        toast.error(result.message || t('errors.sendEmail'));
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.message || t('errors.sendEmail'));
    } finally {
      setPdfActionLoading(null);
    }
  };

  const handleGenerateAndViewPdf = async () => {
    if (!facturation?.id || !selectedDomain?.id) return;
    
    try {
      await generateFacturationPdf(facturation.id, selectedDomain.id);
      toast.success(t('pdfActions.generated'));
      setTimeout(() => handleViewPdf(), 1500);
    } catch (error) {
      toast.error(t('errors.generatePdf'));
    }
  };

  const handleGenerateAndDownloadPdf = async () => {
    if (!facturation?.id || !selectedDomain?.id) return;
    
    try {
      await generateFacturationPdf(facturation.id, selectedDomain.id);
      toast.success(t('pdfActions.generated'));
      setTimeout(() => handleDownloadPdf(), 1500);
    } catch (error) {
      toast.error(t('errors.generatePdf'));
    }
  };
  
  const getStatusInfo = (status) => {
    switch (parseInt(status)) {
      case 1:
        return { label: t('modals.detail.statuses.validated'), class: 'text-success' };
      case 0:
      default:
        return { label: t('modals.detail.statuses.draft'), class: 'text-warning' };
    }
  };

  const calculateTotalTTC = () => {
    if (!facturation) return 0;
    return facturation.totalAuc * (facturation.commissions / 100);
  };

  if (loading) {
    return (
      <div className="modal fade" id="detailFactureModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('loading')}</span>
              </div>
              <p className="mt-2 text-muted">{t('errors.loadDetails')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = facturation ? getStatusInfo(facturation.facturationStatut) : null;
  const totalTTC = calculateTotalTTC();

  return (
    <div className="modal fade" id="detailFactureModal" tabIndex="-1" aria-labelledby="detailFactureModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-facturation text-white">
            <h5 className="modal-title" id="detailFactureModalLabel">
              {t('modals.detail.title')}
            </h5>
            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          
          <div className="modal-body">
            {!facturation ? (
              <div className="text-center py-4">
                <p className="text-muted">{t('errors.loadDetails')}</p>
              </div>
            ) : (
              <div className="row">
                {/* Informations principales */}
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header bg-light">
                      <h6 className="card-title mb-0">{t('modals.detail.sections.general')}</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.id')}</label>
                          <div className="fw-bold">#{facturation.id}</div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.status')}</label>
                          <div className={statusInfo.class}>{statusInfo.label}</div>
                        </div>
                        <div className="col-12 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.client')}</label>
                          <div className="fw-bold fs-6">{facturation.accountName}</div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.invoiceDate')}</label>
                          <div className='text text-primary fw-bold'>{formatDate(facturation.facturationDate)}</div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.creationDate')}</label>
                          <div>{formatDate(facturation.creationDate)}</div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.lastUpdate')}</label>
                          <div>{formatDate(facturation.lastUpdateDate)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Montants et PDF */}
                <div className="col-md-6">
                  <div className="card h-100">
                    <div className="card-header bg-light">
                      <h6 className="card-title mb-0">{t('modals.detail.sections.amounts')}</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.totalAuc')}</label>
                          <div className="fw-bold text-primary fs-5">{formatCurrency(facturation.totalAuc)}</div>
                        </div>
                        <div className="col-6 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.commission')}</label>
                          <div className="fw-bold text-info">{facturation.commissions}%</div>
                        </div>
                        <div className="col-12 mb-3">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.totalTTC')}</label>
                          <div className="fw-bold text-success fs-4">{formatCurrency(totalTTC)}</div>
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-semibold text-muted small">{t('modals.detail.fields.document')}</label>
                          
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            {pdfStatus?.hasPdf ? (
                              <>
                                <button
                                  className="btn btn-info btn-sm"
                                  onClick={handleViewPdf}
                                  disabled={pdfActionLoading === 'view'}
                                >
                                  {pdfActionLoading === 'view' ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  ) : (
                                    <i className="fas fa-eye me-2"></i>
                                  )}
                                  {t('pdfActions.view.label')}
                                </button>

                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={handleDownloadPdf}
                                  disabled={pdfActionLoading === 'download'}
                                >
                                  {pdfActionLoading === 'download' ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  ) : (
                                    <i className="fas fa-download me-2"></i>
                                  )}
                                  {t('pdfActions.download.label')}
                                </button>

                                <div className="btn-group">
                                  <button
                                    className="btn btn-primary btn-sm dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    disabled={pdfActionLoading === 'email'}
                                  >
                                    {pdfActionLoading === 'email' ? (
                                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    ) : (
                                      <i className="fas fa-paper-plane me-2"></i>
                                    )}
                                    {t('pdfActions.email.label')}
                                  </button>
                                  <ul className="dropdown-menu">
                                    <li>
                                      <button 
                                        className="dropdown-item"
                                        onClick={() => handleSendEmail(true)}
                                      >
                                        <i className="fas fa-link text-info me-2"></i>
                                        {t('pdfActions.email.options.secureLink.label')}
                                      </button>
                                    </li>
                                    <li>
                                      <button 
                                        className="dropdown-item"
                                        onClick={() => handleSendEmail(false)}
                                      >
                                        <i className="fas fa-paperclip text-warning me-2"></i>
                                        {t('pdfActions.email.options.attachment.label')}
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </>
                            ) : (
                              <button
                                className="btn btn-warning btn-sm"
                                onClick={handleGeneratePdf}
                                disabled={generatingPdf}
                              >
                                {generatingPdf ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    {t('pdfActions.generating')}
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-file-pdf me-2"></i>
                                    {t('rowActions.generatePdf.label')}
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          
                          {pdfStatus && (
                            <div className="mt-1">
                              <small className={`badge ${pdfStatus.hasPdf ? 'bg-success' : 'bg-warning'}`}>
                                {pdfStatus.hasPdf ? 
                                  t('modals.detail.pdf.available') : 
                                  t('modals.detail.pdf.pending')}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Détails du calcul */}
                <div className="col-12 mt-3">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6 className="card-title mb-0">{t('modals.detail.sections.calculation')}</h6>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>{t('modals.detail.fields.description')}</th>
                              <th className="text-end">{t('modals.detail.fields.amountAuc')}</th>
                              <th className="text-end">{t('modals.detail.fields.commission')}</th>
                              <th className="text-end">{t('modals.detail.fields.commissionAmount')}</th>
                              <th className="text-end">{t('modals.detail.fields.totalTTC')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{t('modals.detail.fields.description')}</td>
                              <td className="text-end">{formatCurrency(facturation.totalAuc)}</td>
                              <td className="text-end">{facturation.commissions}%</td>
                              <td className="text-end">{formatCurrency(totalTTC)}</td>
                              <td className="text-end fw-bold text-success">{formatCurrency(totalTTC)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations supplémentaires */}
                <div className="col-12 mt-3">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6 className="card-title mb-0">{t('modals.detail.sections.additional')}</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-4">
                          <strong>{t('modals.detail.additional.accountId')}:</strong> {facturation.accountId}
                        </div>
                        <div className="col-md-4">
                          <strong>{t('modals.detail.fields.invoiceDate')}:</strong> {formatDate(facturation.facturationDate)}
                        </div>
                        <div className="col-md-4">
                          <strong>{t('modals.detail.additional.technicalStatus')}:</strong> {facturation.facturationStatut}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
              {t('modals.detail.actions.close')}
            </button>
            {pdfStatus?.hasPdf && (
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleViewPdf}
                disabled={pdfActionLoading === 'view'}
              >
                {pdfActionLoading === 'view' ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                ) : (
                  <i className="fas fa-print me-2"></i>
                )}
                {t('modals.detail.actions.print')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailFactureModal;